/**
 * *Už neplatí od* and *Chybné* (PRD-0075 R-C4). No longer valid: since when, why, and where the
 * analyst has it from; on an entity, which of its properties. Wrong: why. Neither deletes: the fact
 * stays, marked, hidden by default, and the mark can be undone.
 */
import { Ban, CalendarX2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { FactField } from './FactField'
import { EMPTY_MARK, type FieldProblem, type MarkValues, markProblems } from './fact-forms'

const T = 'analystFacts'

/** What is marked: an entity or a relationship as wrong, a relationship or a property as no longer valid. */
export type MarkKind =
  | 'retireEntity'
  | 'retireRelationship'
  | 'invalidateRelationship'
  | 'invalidateProperty'

export interface MarkDialogProps {
  kind: MarkKind | null
  onOpenChange: (open: boolean) => void
  /** The properties an entity's *Už neplatí od* may name. */
  keys?: readonly string[]
  submit: { pending: boolean; error: string | null; onSubmit: (values: MarkValues) => void }
}

export function MarkDialog({ kind, onOpenChange, keys = [], submit }: MarkDialogProps) {
  const { t } = useTranslation()
  const retire = kind === 'retireEntity' || kind === 'retireRelationship'
  const Icon = retire ? Ban : CalendarX2
  return (
    <Dialog open={kind !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon aria-hidden className="size-4 text-muted-foreground" />
            {kind && t(`${T}.dialogs.${kind}.title`)}
          </DialogTitle>
          <DialogDescription>{kind && t(`${T}.dialogs.${kind}.description`)}</DialogDescription>
        </DialogHeader>
        {kind && <MarkForm kind={kind} keys={keys} submit={submit} />}
      </DialogContent>
    </Dialog>
  )
}

function MarkForm({
  kind,
  keys,
  submit,
}: { kind: MarkKind } & Pick<MarkDialogProps, 'keys' | 'submit'>) {
  const { t } = useTranslation()
  const needs = { date: kind.startsWith('invalidate'), key: kind === 'invalidateProperty' }
  const [values, setValues] = useState<MarkValues>(EMPTY_MARK)
  const [problems, setProblems] = useState<Partial<Record<keyof MarkValues, FieldProblem>>>({})
  const set = (field: keyof MarkValues) => (value: string) =>
    setValues(current => ({ ...current, [field]: value }))
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const found = markProblems(values, needs)
    setProblems(found)
    if (Object.keys(found).length === 0) submit.onSubmit(values)
  }
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
      {needs.key && (
        <FactField id="mark-key" label={t(`${T}.fields.property`)} problem={problems.key}>
          <Select value={values.key} onValueChange={set('key')}>
            <SelectTrigger id="mark-key" className="w-full">
              <SelectValue placeholder={t(`${T}.fields.propertyPlaceholder`)} />
            </SelectTrigger>
            <SelectContent>
              {(keys ?? []).map(key => (
                <SelectItem key={key} value={key}>
                  {key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FactField>
      )}
      {needs.date && (
        <FactField id="mark-date" label={t(`${T}.fields.invalidFrom`)} problem={problems.date}>
          <Input
            id="mark-date"
            type="date"
            value={values.date}
            onChange={e => set('date')(e.target.value)}
          />
        </FactField>
      )}
      <FactField id="mark-reason" label={t(`${T}.fields.reason`)} problem={problems.reason}>
        <Textarea
          id="mark-reason"
          rows={2}
          value={values.reason}
          onChange={e => set('reason')(e.target.value)}
        />
      </FactField>
      {needs.date && (
        <FactField id="mark-source" label={t(`${T}.fields.sourceText`)}>
          <Input
            id="mark-source"
            value={values.sourceText}
            placeholder={t(`${T}.fields.sourceTextPlaceholder`)}
            onChange={e => set('sourceText')(e.target.value)}
          />
        </FactField>
      )}
      {submit.error && (
        <p role="alert" className="text-xs text-destructive">
          {submit.error}
        </p>
      )}
      <DialogFooter>
        <Button
          type="submit"
          variant={needs.date ? 'default' : 'destructive'}
          disabled={submit.pending}
        >
          {submit.pending && <Loader2 aria-hidden className="animate-spin" />}
          {t(`${T}.dialogs.${kind}.submit`)}
        </Button>
      </DialogFooter>
    </form>
  )
}
